import {
  Box,
  Button,
  chakra,
  Flex,
  HStack,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Checkbox,
  Heading,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  VStack,
} from "@chakra-ui/react";

import generateExcel from "zipcelx";
import {
  fuzzyFilter,
  isDateLessThanFilter,
  userFilter,
  arrayFilter,
} from "./filter";

import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { cloneElement, useState } from "react";

import {
  FiChevronDown,
  FiChevronUp,
  FiDownload,
  FiFilter,
} from "react-icons/fi";

import { EmptyState, ErrorBoundary, LoadingSpinner } from "@saas-ui/react";
import { ColumnFilter, FilteredValues, GlobalFilter } from "./Filters";
import { Paginator } from "./Paginator";
import { useCompareContext } from "../components/media/compare/CompareMedia";
import { SentimentBadge, SentimentBar } from "../components/media/metrics/Sentiment";

const CompareTable = ({
  data,
  columns,
  columnsToFilter = [],
  isLoading,
  handleLink,
  title = "",
  isExportable = true,
  isSelectable = false,
  simplify = false,
  isSearchable = true,
  otherComponents,
  children,
  exportOptions = ['xls'],
  // selectedValues,
  ...props
}) => {
  const [globalFilter, setGlobalFilter] = useState("");
  const initialFocusRef = React.useRef();
  const [rowSelection, setRowSelection] = useState({});

  let { selectedValues } = useCompareContext();

  const table = useReactTable({
    columns: columns,
    data: data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: fuzzyFilter,
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: isSelectable,
    onRowSelectionChange: setRowSelection,
    initialState: { pagination: { pageSize: 20 } },
    state: { globalFilter, rowSelection },
    filterFns: {
      fuzzy: fuzzyFilter,
      dateFilter: isDateLessThanFilter,
      user: userFilter,
      array: arrayFilter,
    },
    enableSortingRemoval: false,
  });

  return isLoading ? (
    <LoadingSpinner />
  ) : !!data ? (
    <Flex direction="column" height="100%" width="100%">
      {!simplify && (
        <HStack my="3" px="3" justifyContent="space-between">
          <HStack>
            {title && (
              <Heading ml="3" size="sm">
                {title}
              </Heading>
            )}
            {children}
            {columnsToFilter && (
              <>
                <Popover initialFocusRef={initialFocusRef} placement="bottom">
                  {({ isOpen, onClose }) => (
                    <Box ml={3} width="auto">
                      <PopoverTrigger>
                        <Button size="xs" variant="outline">
                          <FiFilter />
                          <Text ml="1">Filter</Text>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent width="100%">
                        <Tabs>
                          <TabList>
                            {columnsToFilter?.map((cc) => (
                              <Tab key={`tabh-${cc}`}>
                                <Text fontSize="sm">
                                  {table.getColumn(cc).columnDef?.header}
                                </Text>
                              </Tab>
                            ))}
                          </TabList>
                          <TabPanels>
                            {columnsToFilter?.map((cc) => (
                              <TabPanel key={`tab-${cc}`} p="0">
                                <ColumnFilter
                                  column={table.getColumn(cc)}
                                  table={table}
                                  close={onClose}
                                />
                              </TabPanel>
                            ))}
                          </TabPanels>
                        </Tabs>
                      </PopoverContent>
                    </Box>
                  )}
                </Popover>
              </>
            )}
          </HStack>
          <HStack>
            {isSearchable && (
              <GlobalFilter
                value={table.getState().globalFilter}
                filterFn={setGlobalFilter}
              />
            )}
            {/* {isExportable && (
              <Button onClick={() => getExcel()} variant="outline">
                <HStack m="3">
                  <FiDownload />
                  <Text mr=".5">Export</Text>
                </HStack>
              </Button>
            )} */}
            {otherComponents && otherComponents}
          </HStack>
        </HStack>
      )}
      <HStack
        borderTop={simplify ? "0" : "1px"}
        color="gray.200"
        justifyContent="space-between"
      >
        <FilteredValues columnsToFilter={columnsToFilter} table={table} />
        <HStack>
          {props?.tableSummary && cloneElement(props?.tableSummary, { table })}
        </HStack>
      </HStack>
      <Box flex="auto" overflowY="auto">
        <Table
          size="sm"
          variant={simplify ? "unstyled" : "simple"}
        >
          <Tbody>
            {table.getRowModel()?.rows?.map((_, index) => {
              const topic = data[index];
              const headers = [topic?.topic || "", ...columns.slice(1).map(({ header }) => header)];
              return (
                <>
                  <Tr>
                    {headers.map((header, index) => {
                      return (
                        <>
                          {index === 0 && (<Th>&nbsp;</Th >)}
                          <Th
                            py="3"
                            pl="2"
                            key={`header-${index}`}
                          >
                            <HStack>
                              <Text
                                fontWeight="bold"
                                whiteSpace="nowrap"
                              >
                                {header}
                              </Text>
                            </HStack>
                          </Th>
                        </>
                      );
                    })}
                  </Tr >
                  {(topic?.media || []).map((media, mediaIndex) => {
                    return (
                      <Tr
                        key={`tr-${index}-${mediaIndex}`}
                        _hover={
                          handleLink && {
                            background: "gray.50",
                            cursor: "pointer",
                          }
                        }
                      >
                        {columns.map((cell, cellIndex) => {
                          const selectedItem = selectedValues.find(
                            (item) => item.media_item_id === media?.id
                          )
                          const item = media[cell.accessorKey]
                          return (
                            <React.Fragment key={`${index}-${mediaIndex}-${cellIndex}`}>
                              {cellIndex === 0 && (
                                <Td>{selectedItem?.subtitle || ''}</Td>
                              )}
                              <Td py={simplify ? "1" : "3"} overflow="hidden" bg="white">
                                {cell.accessorKey === 'total' ? (
                                  <SentimentBar positive={media.positive} negative={media.negative} />
                                ) : cell.accessorKey === 'description' ? (
                                  <VStack justifyContent="start" alignItems="start" gap={2}>
                                    <Text noOfLines={2}>{item}</Text>
                                  </VStack>
                                ) : cell.accessorKey === 'sentiment' ? (
                                  <SentimentBadge sentiment={item} />
                                ) : (
                                  <Text>
                                    {item}
                                    {typeof item === 'number' ? '%' : ''}
                                  </Text>
                                )}
                              </Td>
                            </React.Fragment>

                          );
                        })}
                      </Tr >
                    )
                  })
                  }
                  <Tr>
                    <Th height={'100px'}>&nbsp;</Th>
                  </Tr>
                </>
              )
            })}
          </Tbody >
        </Table>
      </Box>
      {!simplify && <Paginator table={table} />}
    </Flex >
  ) : (
    <EmptyState
      title="No Data"
      description="There is no data to display"
      actions={<>{otherComponents && otherComponents}</>}
    />
  );
};

export default CompareTable;
